// Members.tsx — workspace-members cycle (THI-110 Task 5, .superpowers/sdd/
// 2026-08-20-workspace-members/). Workspace-level screen (Billing.tsx idiom: header comment +
// useQuery + skeleton sized to the real layout + error state) — the members table, the
// pending-invites table, and the "Invite member" dialog, all against Task 3's `/api/members`
// endpoints verbatim.
//
// `GET /api/members` is owner|admin-gated server-side (`routes/members.ts`'s `managed` sub-
// router), so reaching this page as a plain member 403s. The workspace menu already hides the
// "Members" item for a non-owner/admin (`WorkspaceMenu.tsx`), but a direct `/members` deep
// link is still reachable — that specific status is caught below and rendered as a friendly
// permission card instead of the generic `error.message` text every other `error &&` branch on
// this screen falls back to. Reaching data at all is therefore proof the caller is owner|admin
// — no separate client-side role check is needed to decide whether the role `<select>`/Remove
// controls should render.
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UsersIcon } from 'lucide-react';
import type {
  MemberRole,
  MembersOverviewDto,
  WorkspaceInviteDto,
  WorkspaceMemberDto,
} from '@repo/shared-types';
import { Button } from '@repo/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@repo/ui/components/dialog';
import { EmptyState } from '@repo/ui/components/empty-state';
import { Input } from '@repo/ui/components/input';
import { Label } from '@repo/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { PageHeader } from '../components/PageHeader';
import { useSession } from '../lib/auth-client';
import { apiFetch, ApiError } from '../lib/api';
import { relativeTime } from '../lib/relative-time';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

const MEMBERS_QUERY_KEY = ['members'] as const;

type EditableRole = Exclude<MemberRole, 'owner'>;

function roleLabel(role: MemberRole, t: Catalog): string {
  return { owner: t.members.roleOwner, admin: t.members.roleAdmin, member: t.members.roleMember }[
    role
  ];
}

/** Plain muted span, not `StatusChip` — role isn't a status, same call `AdminPlayground.tsx`
 * made for feedback `type` (its own doc comment: "plain muted"). */
function RoleChip({ role, t }: { role: MemberRole; t: Catalog }) {
  return (
    <span className="inline-flex w-fit items-center rounded-full bg-muted px-2.5 py-[3px] text-xs font-medium text-muted-foreground">
      {roleLabel(role, t)}
    </span>
  );
}

/** Sized to the members table (34px header + three 44px rows) and the invites table beneath
 * it — same `TableSkeleton` shape as `AdminFeedback.tsx`, doubled. */
function MembersSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col overflow-hidden rounded-lg border">
        <div className="h-[34px] bg-muted" />
        {[0, 1, 2].map((i) => (
          <div key={i} className={`flex h-11 items-center gap-6 px-4 ${i === 0 ? '' : 'border-t'}`}>
            <SkeletonText className="w-32" />
            <SkeletonText className="w-48" />
            <SkeletonText className="w-16" />
          </div>
        ))}
      </div>
      <Skeleton className="h-5 w-36" />
      <div className="flex flex-col overflow-hidden rounded-lg border">
        <div className="h-[34px] bg-muted" />
        <div className="flex h-11 items-center px-4">
          <SkeletonText className="w-56" />
        </div>
      </div>
    </div>
  );
}

/** The one row the "Remove"/"Leave workspace" confirm dialog is currently open for —
 * `isSelf` decides both the dialog copy and, on success, whether the mutation ends in a hard
 * navigation (see `removeMutation` below). */
interface ConfirmTarget {
  member: WorkspaceMemberDto;
  isSelf: boolean;
}

export function Members() {
  const t = useT();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const userId = session?.user.id;

  const { data, isPending, error } = useQuery({
    queryKey: MEMBERS_QUERY_KEY,
    queryFn: () => apiFetch<MembersOverviewDto>('/api/members'),
    // Browser-QA finding (2026-08-21): a plain member deep-linking here gets a DETERMINISTIC
    // 403 — the default 3-retry backoff meant ~10s of skeleton before the friendly
    // members-only card appeared. 4xx never heals on retry; fail straight to the card.
    retry: (failureCount, err) =>
      !(err instanceof ApiError && err.status >= 400 && err.status < 500) && failureCount < 3,
  });

  function invalidate() {
    return queryClient.invalidateQueries({ queryKey: MEMBERS_QUERY_KEY });
  }

  // Per-row role change — same `variables`-keyed pending/error idiom as `AdminFeedback.tsx`'s
  // `toggleHandled`.
  const [roleErrors, setRoleErrors] = useState<Record<string, string>>({});
  const roleMutation = useMutation({
    mutationFn: ({ id, role }: { id: string; role: EditableRole }) =>
      apiFetch<WorkspaceMemberDto>(`/api/members/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role }),
      }),
    onSuccess: (_data, { id }) => {
      setRoleErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      void invalidate();
    },
    onError: (err, { id }) =>
      setRoleErrors((prev) => ({
        ...prev,
        [id]: err instanceof ApiError ? err.message : t.members.roleChangeErrorFallback,
      })),
  });

  // Remove (others) / Leave (self, non-owner) share one confirm dialog and one mutation — the
  // only difference is copy and, on a self-leave, what happens afterward. `isSelf` travels as
  // part of the mutation's own variables (not read back off `confirmTarget` inside `onSuccess`)
  // so there's no risk of that closure racing a state update made earlier in the same callback.
  const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const removeMutation = useMutation({
    mutationFn: ({ id }: { id: string; isSelf: boolean }) =>
      apiFetch<{ deleted: true }>(`/api/members/${id}`, { method: 'DELETE' }),
    onSuccess: (_data, { isSelf }) => {
      setConfirmTarget(null);
      setRemoveError(null);
      if (isSelf) {
        // Leaving changes which workspace every OTHER query on this page (and every other
        // route) resolves against — same "hard nav across a workspace/session boundary"
        // rationale as `SignIn.tsx`'s post-auth `location.assign`, rather than an in-place
        // cache invalidation that would leave stale this-workspace data behind.
        location.assign('/');
        return;
      }
      void invalidate();
    },
    onError: (err) =>
      setRemoveError(err instanceof ApiError ? err.message : t.members.removeErrorFallback),
  });

  // Revoke a pending invite.
  const [revokeErrors, setRevokeErrors] = useState<Record<string, string>>({});
  const revokeMutation = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ revoked: true }>(`/api/members/invites/${id}`, { method: 'DELETE' }),
    onSuccess: (_data, id) => {
      setRevokeErrors((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      void invalidate();
    },
    onError: (err, id) =>
      setRevokeErrors((prev) => ({
        ...prev,
        [id]: err instanceof ApiError ? err.message : t.members.revokeErrorFallback,
      })),
  });

  // Invite dialog.
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<EditableRole>('member');
  const [inviteError, setInviteError] = useState<string | null>(null);
  // Drives the 422 PLAN_LIMIT upgrade CTA below — same idiom `Bots.tsx`'s bot-cap gate uses
  // (an inline message plus a `Link` to `/billing`), branched on the error CODE rather than
  // string-matching the message so a copy change server-side can't silently drop the CTA.
  const [inviteErrorCode, setInviteErrorCode] = useState<string | null>(null);
  const inviteMutation = useMutation({
    mutationFn: () =>
      apiFetch<WorkspaceInviteDto>('/api/members/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      }),
    onSuccess: async () => {
      setInviteOpen(false);
      setInviteEmail('');
      setInviteRole('member');
      setInviteError(null);
      setInviteErrorCode(null);
      await invalidate();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setInviteError(err.message);
        setInviteErrorCode(err.code);
      } else {
        setInviteError(t.members.inviteErrorFallback);
        setInviteErrorCode(null);
      }
    },
  });

  function openInviteDialog() {
    setInviteEmail('');
    setInviteRole('member');
    setInviteError(null);
    setInviteErrorCode(null);
    setInviteOpen(true);
  }

  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t.members.pageTitle} />
        <EmptyState
          icon={UsersIcon}
          title={t.members.membersOnlyTitle}
          description={t.members.membersOnlyDescription}
        />
      </div>
    );
  }

  const seats = data?.seats;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t.members.pageTitle}
        subtitle={
          seats &&
          // Never recomputed from `PLAN_LIMITS` client-side (CLAUDE.md) — `seats` is already
          // the server's derived shape; `cap: null` means unlimited (that field's own doc
          // comment, `dto.ts`).
          t.members.seatsSubtitle({
            used: seats.used,
            cap: seats.cap === null ? t.members.unlimitedCapWord : seats.cap,
            pending: seats.pending,
          })
        }
        action={<Button onClick={openInviteDialog}>{t.members.inviteMemberCta}</Button>}
      />

      {isPending && <MembersSkeleton />}
      {error && <p className="text-sm text-destructive">{error.message}</p>}

      {data && (
        <>
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t.members.colName}</TableHead>
                  <TableHead>{t.members.colEmail}</TableHead>
                  <TableHead>{t.members.colRole}</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.members.map((member) => {
                  const isSelf = member.userId === userId;
                  return (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">{member.name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        {member.role === 'owner' ? (
                          <RoleChip role="owner" t={t} />
                        ) : (
                          <div className="flex flex-col gap-1">
                            <Select
                              value={member.role}
                              onValueChange={(value) =>
                                roleMutation.mutate({ id: member.id, role: value as EditableRole })
                              }
                            >
                              <SelectTrigger size="sm" className="w-28">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="member">{t.members.roleOptionMember}</SelectItem>
                                <SelectItem value="admin">{t.members.roleOptionAdmin}</SelectItem>
                              </SelectContent>
                            </Select>
                            {roleErrors[member.id] && (
                              <span className="text-xs text-destructive">{roleErrors[member.id]}</span>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.role !== 'owner' && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-transparent hover:text-destructive"
                            onClick={() => {
                              setRemoveError(null);
                              setConfirmTarget({ member, isSelf });
                            }}
                          >
                            {isSelf ? t.members.leaveWorkspaceCta : t.members.removeCta}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold">{t.members.pendingInvitesHeading}</h2>
            {data.invites.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t.members.noPendingInvites}</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t.members.inviteColEmail}</TableHead>
                      <TableHead>{t.members.inviteColRole}</TableHead>
                      <TableHead>{t.members.inviteColExpires}</TableHead>
                      <TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.invites.map((invite) => {
                      const pending = revokeMutation.isPending && revokeMutation.variables === invite.id;
                      return (
                        <TableRow key={invite.id}>
                          <TableCell>{invite.email}</TableCell>
                          <TableCell>
                            <RoleChip role={invite.role} t={t} />
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {relativeTime(invite.expiresAt, t.time)}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col items-start gap-1">
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                disabled={pending}
                                onClick={() => revokeMutation.mutate(invite.id)}
                              >
                                {pending ? t.members.revokingCta : t.members.revokeCta}
                              </Button>
                              {revokeErrors[invite.id] && (
                                <span className="text-xs text-destructive">{revokeErrors[invite.id]}</span>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </>
      )}

      <Dialog
        open={confirmTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmTarget(null);
            setRemoveError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmTarget?.isSelf
                ? t.members.leaveDialogTitle
                : t.members.removeDialogTitle({ name: confirmTarget?.member.name ?? '' })}
            </DialogTitle>
            <DialogDescription>
              {confirmTarget?.isSelf
                ? t.members.leaveDialogDescription
                : t.members.removeDialogDescription({ name: confirmTarget?.member.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          {removeError && <p className="text-sm text-destructive">{removeError}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmTarget(null)}>
              {t.members.cancelCta}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={removeMutation.isPending}
              onClick={() =>
                confirmTarget &&
                removeMutation.mutate({ id: confirmTarget.member.id, isSelf: confirmTarget.isSelf })
              }
            >
              {removeMutation.isPending
                ? t.members.removingCta
                : confirmTarget?.isSelf
                  ? t.members.leaveWorkspaceCta
                  : t.members.removeCta}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.members.inviteMemberCta}</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!inviteEmail.trim()) return;
              setInviteError(null);
              setInviteErrorCode(null);
              inviteMutation.mutate();
            }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-2">
              <Label htmlFor="invite-email">{t.members.emailLabel}</Label>
              <Input
                id="invite-email"
                type="email"
                autoFocus
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t.members.emailPlaceholder}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="invite-role">{t.members.roleLabel}</Label>
              <Select
                value={inviteRole}
                onValueChange={(value) => setInviteRole(value as EditableRole)}
              >
                <SelectTrigger id="invite-role" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">{t.members.roleOptionMember}</SelectItem>
                  <SelectItem value="admin">{t.members.roleOptionAdmin}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {inviteError && (
              <div className="flex flex-col gap-1">
                <p className="text-sm text-destructive">{inviteError}</p>
                {inviteErrorCode === 'PLAN_LIMIT' && (
                  <Link to="/billing" className="text-sm font-semibold hover:underline">
                    {t.bots.comparePlansLink}
                  </Link>
                )}
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={inviteMutation.isPending || !inviteEmail.trim()}>
                {inviteMutation.isPending ? t.members.sendingInviteCta : t.members.sendInviteCta}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
